import { useContext, useEffect, useState } from 'react';
import { getCookie } from 'cookies-next';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { showSoldAccountSection } from '@/components/customercenter-ui/utils/assetSaleUtils';
import { useCheckout } from '@/context/checkoutProvider';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import { useAccount } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { isCreditSite, TranslateText } from '@/helpers/utils/siteUtils';
import { CustomerDetailsPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { COOKIE_KEYS, DEFAULT_SITE_LANG } from '../../../../../../constants/constant';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/customAccordian/customAccordian';
import { Skeleton } from '../../../ui/skeleton';
import { RESPONSE_SUCCESS_MESSAGE } from '../../constants';
import { PageTexts } from '../../i18n';
import { CreditSummary } from '../../types';
import { transformCreditSummaryData } from '../../utils';

const CustomerCreditSummary = () => {
  const { getCustomerDetails } = useAccount();
  const projectSettings: ProjectSettings = useProjSettings();
  const [creditSummary, setCreditSummary] = useState<CreditSummary>();
  const [loading, setLoading] = useState(false);
  const [isSoldAccount, setIsSoldAccount] = useState(false);
  const [isCurrentSiteCreditSite, setIsCurrentSiteCreditSite] = useState(false);
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const { customerData, setCustomerData, setCustomerDetailsResponse } = useContext(CreditSummaryContext);
  const { customerDetailsResponse: checkoutCustomerDetails } = useCheckout();

  useEffect(() => {
    setIsCurrentSiteCreditSite(isCreditSite());
  }, []);

  useEffect(() => {
    const isSoldAccount = customerData?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.isSoldAccount ?? false;
    setIsSoldAccount(isSoldAccount);
  }, [customerData]);

  const getCreditDetails = async (data: any) => {
    setLoading(true);
    const payload = {
      query: {
        customerId: data?.customerId,
        omsCustomerId: data?.omsCustomerId,
        divisionCode: data?.divisionCode,
        idName: data?.idName,
      },
    } as CustomerDetailsPayload;
    await getCustomerDetails(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        setCustomerData(response?.Customer);
        const creditSummary = transformCreditSummaryData(response?.Customer?.DivisionCustomers?.[0] ?? {});
        setCreditSummary(creditSummary);
        setCustomerDetailsResponse(response);
      }
    });
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      if (customerData) {
        const creditSummary = transformCreditSummaryData(customerData?.DivisionCustomers?.[0] ?? {});
        setCreditSummary(creditSummary);
      } else if (checkoutCustomerDetails?.code === RESPONSE_SUCCESS_MESSAGE && checkoutCustomerDetails?.Customer) {
        setCustomerData(checkoutCustomerDetails.Customer);
        setCustomerDetailsResponse(checkoutCustomerDetails);
        const creditSummary = transformCreditSummaryData(
          checkoutCustomerDetails.Customer?.DivisionCustomers?.[0] ?? {},
        );
        setCreditSummary(creditSummary);
      } else {
        const current_CDP_Data = getCookie(COOKIE_KEYS.CDP_URL) ?? '';
        if (current_CDP_Data.length) {
          const data = JSON.parse(current_CDP_Data);
          getCreditDetails(data);
        }
      }
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [customerData, checkoutCustomerDetails]);

  return isCurrentSiteCreditSite ? (
    <Accordion
      type="single"
      collapsible
      defaultValue="item-1"
      className="w-full rounded-md border border-foggyGray px-5 py-1 shadow-lg"
    >
      <AccordionItem value="item-1" className="border-none">
        <AccordionTrigger className="text-lg font-bold hover:no-underline">{Texts.creditSummary}</AccordionTrigger>
        <AccordionContent>
          {loading ? (
            <section className="flex flex-col gap-4">
              <Skeleton className="h-[40px] w-full rounded-md bg-foggyGray" />
              <Skeleton className="h-[40px] w-full rounded-md bg-foggyGray" />
              <Skeleton className="h-[40px] w-full rounded-md bg-foggyGray" />
              <Skeleton className="h-[40px] w-full rounded-md bg-foggyGray" />
              <Skeleton className="h-[40px] w-full rounded-md bg-foggyGray" />
              <Skeleton className="h-[40px] w-full rounded-md bg-foggyGray" />
              <Skeleton className="h-[40px] w-full rounded-md bg-foggyGray" />
              <Skeleton className="h-[100px] w-full rounded-md bg-foggyGray" />
            </section>
          ) : !showSoldAccountSection(projectSettings, isSoldAccount) && creditSummary ? (
            creditSummary?.map((items) => (
              <div
                key={`key-${items?.label}`}
                className={`flex pb-5 text-sm ${
                  items?.label.includes('Total Balance') || items?.label.includes('Internet Credit Offer')
                    ? 'font-bold'
                    : 'font-normal'
                }`}
              >
                <p className="mr-auto">{items?.label}</p>
                <p>{items?.value}</p>
              </div>
            ))
          ) : (
            isSoldAccount && (
              <div className="flex pb-5 text-sm">
                <p className="mr-auto">Account Status</p>
                <p>Sold</p>
              </div>
            )
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  ) : (
    <></>
  );
};

export default CustomerCreditSummary;
